/* Two-level approval of a report period: Finance first, then the Account Manager (AM). Pure functions over the
 * report_approval rows (see db/schema.sql), so the same rules carry over to a real database unchanged.
 *
 * report_approval is an append-only log: every approval AND every removal of an approval is a row. The `approval` column says what
 * happened: 'finance' / 'account_manager' (approved) or 'finance_unapproved' / 'account_manager_unapproved' (approval removed, with the
 * reason in `comments`). A level is approved when the latest row for that level and period is an approval, so the history is never lost. */
import { isManagerOf, managerOf } from './constants.js';

export const FINANCE = 'finance';
export const AM = 'account_manager';
export const LEVEL_NAME = { [FINANCE]: 'Finance', [AM]: 'Account Manager' };
export const UNAPPROVED = '_unapproved';
/** 'finance_unapproved' -> 'finance' (the level a row is about, whether it approves or removes). */
export const levelOf = (row) => String(row.approval || '').replace(UNAPPROVED, '');
export const isRemoval = (row) => String(row.approval || '').endsWith(UNAPPROVED);

const sameReport = (a, r) => a.settlement_report_id === r.settlement_report_id && a.report_year === r.report_year && a.as_at_date === r.as_at_date;
const samePeriod = (a, p) => a.period_type === p.type && a.period_start === p.start;

/** The approvals that stand: for each level of a report period, its latest row if that row is an approval (not a removal). */
function standing(mine) {
  const out = { [FINANCE]: null, [AM]: null };
  mine.forEach((a) => { const l = levelOf(a); if (l in out) out[l] = isRemoval(a) ? null : a; });   // rows are in the order they happened
  return out;
}

/** { finance: row|null, account_manager: row|null, stage: 'none' | 'finance' | 'complete' } for one report period. */
export function statusOf(rows, report, period) {
  const { finance, account_manager: am } = standing(rows.filter((a) => sameReport(a, report) && samePeriod(a, period)));
  return { finance, account_manager: am, stage: finance && am ? 'complete' : finance ? 'finance' : 'none' };
}

/** Every report period that has an approval standing -> [{ key, id, year, asAt, type, label, start, title, finance, account_manager }]. */
export function standingApprovals(rows) {
  const by = new Map();
  rows.forEach((a) => {
    const k = [a.settlement_report_id, a.report_year, a.as_at_date, a.period_type, a.period_start].join('|');
    if (!by.has(k)) by.set(k, { key: k, id: a.settlement_report_id, year: a.report_year, asAt: a.as_at_date, type: a.period_type, label: a.period_label, start: a.period_start, rows: [] });
    by.get(k).rows.push(a);
  });
  return [...by.values()].map(({ rows: mine, ...g }) => ({ ...g, title: periodTitle(g), ...standing(mine) })).filter((g) => g.finance || g.account_manager);
}
export const periodTitle = (a) => (a.type === 'year' || a.period_type === 'year' ? `Annual ${a.label ?? a.period_label}` : `${a.label ?? a.period_label} ${String(a.start ?? a.period_start).slice(0, 4)}`);

/** Can this user give this level of approval now? -> { ok, reason } (reason is shown to the user when not ok). */
/** Why a signed-in Account Manager has no account manager name: said plainly, instead of "only <name> can approve". */
export const notListedReason = (user) => (user.manager_status === 'error'
  ? 'The account manager list (account_manager_email) could not be read, so the app cannot tell which reports are yours. Tell your administrator.'
  : `Your e-mail address${user.email ? ` (${user.email})` : ''} is not listed as an account manager (account_manager_email), so you cannot give or remove AM approval.`);

export function canApprove(user, level, report, status) {
  if (status[level]) return { ok: false, reason: 'Already approved.' };
  if (!user) return { ok: false, reason: 'Your account is not set up as an approver.' };
  if (level === FINANCE) {
    return user.role === FINANCE ? { ok: true } : { ok: false, reason: 'Only Finance can give Finance approval.' };
  }
  if (!status.finance) return { ok: false, reason: 'Waiting for Finance approval first.' };
  const owner = managerOf(report);
  if (level === AM) {
    return user.role === AM ? { ok: true } : { ok: false, reason: 'Only Account Managers can give Account Manager approval.' };
  }
  //if (user.role !== AM) return { ok: false, reason: 'Only the Account Manager can give AM approval.' };
  if (!String(user.account_manager || '').trim()) return { ok: false, reason: notListedReason(user) };
  //if (!isManagerOf(user, report)) return { ok: false, reason: `Only ${owner}, the account manager for this report, can give AM approval.` };
  return { ok: true };
}

/** Appends an approval to the db document (mutates `db`) and returns the new row. Throws if the rules are not met. */
export function addApproval(db, { level, report, period, user, now = new Date() }) {
  const status = statusOf(db.report_approval, report, period);
  const check = canApprove(user, level, report, status);
  if (!check.ok) throw new Error(check.reason);
  const next = db.report_approval.reduce((m, a) => Math.max(m, Number(String(a.approval_id).replace(/\D/g, '')) || 0), 0) + 1;
  const row = {
    approval_id: `apr-${String(next).padStart(4, '0')}`,
    settlement_report_id: report.settlement_report_id, report_year: report.report_year, as_at_date: report.as_at_date,
    period_type: period.type, period_label: period.label, period_start: period.start,
    approval: level, actioned_by_user_id: user.user_id, actioned_by_name: user.display_name, actioned_at: now.toISOString(), comments: null,
  };
  db.report_approval.push(row);
  return row;
}

/** Can this user take this approval away (Admin)? Finance can remove either level; an Account Manager only their own reports' Account Manager approval.
 * -> { ok, reason } */
export function canUnapprove(user, level, report, status) {
  if (!status[level]) return { ok: false, reason: 'Not approved.' };
  if (!user) return { ok: false, reason: 'Your account is not set up as an approver.' };
  if (user.role === FINANCE) return { ok: true };
  if (user.role !== AM) return { ok: false, reason: 'Only Finance or an Account Manager can unapprove.' };
  if (level === FINANCE) return { ok: false, reason: 'Only Finance can remove Finance approval.' };
  const owner = managerOf(report);
  if (!String(user.account_manager || '').trim()) return { ok: false, reason: notListedReason(user) };
  if (!isManagerOf(user, report)) return { ok: false, reason: `Only ${owner}, the account manager for this report, can remove AM approval.` };
  return { ok: true };
}

/** Takes an approval away (mutates `db`) by adding a removal row to report_approval: who, when and why (`comments`) are kept, and the original
 * approval row stays in the log. Removing Finance approval also removes the Account Manager approval that depended on it.
 * Returns the new rows. Throws if not allowed. */
export function removeApproval(db, { level, report, period, user, reason, now = new Date() }) {
  const status = statusOf(db.report_approval, report, period);
  const check = canUnapprove(user, level, report, status);
  if (!check.ok) throw new Error(check.reason);
  const why = String(reason || '').trim();
  if (!why) throw new Error('Give a reason for removing the approval.');
  const levels = level === FINANCE ? [FINANCE, AM].filter((l) => status[l]) : [level];
  let next = db.report_approval.reduce((m, a) => Math.max(m, Number(String(a.approval_id).replace(/\D/g, '')) || 0), 0);
  const rows = levels.map((l) => ({
    approval_id: `apr-${String(++next).padStart(4, '0')}`,
    settlement_report_id: report.settlement_report_id, report_year: report.report_year, as_at_date: report.as_at_date,
    period_type: period.type, period_label: period.label, period_start: period.start,
    approval: l + UNAPPROVED, actioned_by_user_id: user.user_id, actioned_by_name: user.display_name, actioned_at: now.toISOString(), comments: why,
  }));
  db.report_approval.push(...rows);
  return rows;
}

export const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso || '') : new Intl.DateTimeFormat('en-GB', { dateStyle: 'short', timeStyle: 'short' }).format(d).replace(',', '');
};
export const describe = (row) => (row ? `${row.actioned_by_name}, ${fmtWhen(row.actioned_at)}` : null);
export const stageLabel = (stage) => (stage === 'complete' ? 'Approved' : stage === 'finance' ? 'Finance approved' : 'Not approved');
